import { z } from "zod";

/*
 * DynamoDBの型
 */
const DDBUserProductInfoSchema = z.object({
  productId: z.string(),
  lastLoginDate: z.string(), // ISO文字列
});
export type DDBUserProductInfo = z.infer<typeof DDBUserProductInfoSchema>;
export interface DDBUser {
  PK: string; // USER#<sub>
  userId: string; // sub
  name: string;
  cognitoUsername: string;
  email: string;
  status: string;
  updatedAt: string;
  createdAt: string;
  currentTenantId?: string;
  productsInfo?: DDBUserProductInfo[];
  lastPasskeyPromptedAt?: string; // PRODUCT_DEV-396 パスキー登録促進モーダルの最終表示日時 (ISO 8601)
  /**
   * PRODUCT_DEV-396 パスキー登録済デバイスのキー一覧
   * 各要素は normalizeUserAgent(ua) の結果 (例: "Safari:iOS", "Chrome:macOS")
   * 最大10件、超過時は古い順に削除 (FIFO)
   */
  passkeyDeviceKeys?: string[];
  /**
   * PRODUCT_DEV-421 エイリアス
   * テナント内ユニーク（テナント間は重複OK）。3-30文字、半角英数字・. _ -
   * admin が新規作成時または既存ユーザに対して一度だけ設定可能。設定後は変更不可
   */
  alias?: string;
  /**
   * PRODUCT_DEV-615 顔写真（アバター）の S3 オブジェクトキー。
   * 全 product 共通。key 規約 = `user-avatars/<tenantId>/<userId>/<version>.<ext>`
   * （テナント×ユーザでスコープ・version でイミュータブル）。実体は avatar 専用バケットに置き、
   * 配信は **presigned S3 GET**（CloudFront 不採用）。未設定＝アバター無し（イニシャル表示）。
   * 追加のみの optional。login/token/redirect など既存認証経路には露出しない
   * （whoami/tenantUsers への写像で明示的に足したときだけ表に出る）。
   */
  faceImageKey?: string;
}

// PRODUCT_DEV-421 エイリアス Zod スキーマ
export const ALIAS_REGEX = /^[a-zA-Z0-9._-]+$/;
export const AliasSchema = z
  .string()
  .min(3, "エイリアスは3文字以上で入力してください")
  .max(30, "エイリアスは30文字以内で入力してください")
  .regex(
    ALIAS_REGEX,
    "エイリアスは英数字、ドット、ハイフン、アンダースコアのみ使用できます",
  );

// ユーザ追加時のスキーマ
export const DDBCreateUserSchema = z.object({
  name: z.string().min(1, "名前は必須です"),
  email: z.string().email("有効なメールアドレスを入力してください").optional(),
  username: z.string().min(1, "ユーザーIDは必須です").optional(),
  alias: AliasSchema.optional(),
});
export const DDBCreateUserTenantSchema = z.object({
  role: z.enum(["admin", "member", "viewer"], {
    errorMap: () => ({ message: "無効な権限です" }),
  }),
});
export type DDBCreateUser = z.infer<typeof DDBCreateUserSchema>;
export type DDBCreateUserTenant = z.infer<typeof DDBCreateUserTenantSchema>;

// ユーザのカレントテナント更新スキーマ
export const DDBUserPatchCurrentTenantSchema = z.object({
  currentTenantId: z.string(),
  productsInfo: z.array(DDBUserProductInfoSchema).optional(),
});

export type DDBPatchCurrentTenant = z.infer<
  typeof DDBUserPatchCurrentTenantSchema
>;

// PRODUCT_DEV-396 ユーザ自身によるパスキー関連設定の更新スキーマ
export const DDBUserPatchSecuritySettingsSchema = z.object({
  lastPasskeyPromptedAt: z.string().optional(),
  // true: 現在のリクエスト UA を passkeyDeviceKeys に追加する (registerPasskey 成功後)
  addPasskeyDevice: z.boolean().optional(),
  // true: passkeyDeviceKeys を全クリアする (deletePasskey で全削除した場合)
  clearPasskeyDevices: z.boolean().optional(),
});

export type DDBPatchSecuritySettings = z.infer<
  typeof DDBUserPatchSecuritySettingsSchema
>;

/**
 * PRODUCT_DEV-1070 会社の「初期表示ページ」（Unizo の HOME の既定）。
 * 個人設定（Unizo の UserPreferences.homeScreen）が優先し、どちらも無ければマイ手帳。
 * Unizo の手帳を使っている会社（featureFlags.diaryHome）だけが読む。
 */
export const TenantHomeScreenSchema = z.enum(["diary", "meetings"]);
export type TenantHomeScreen = z.infer<typeof TenantHomeScreenSchema>;

export interface DDBTenant {
  tenantId: string; // partition key
  tenantName: string;
  status: string;
  mfaRequired?: boolean; // テナント単位MFA強制フラグ
  passkeyEnabled?: boolean; // PRODUCT_DEV-396 テナント単位パスキー利用許可フラグ (オプトイン)
  // PRODUCT_DEV-611 本社都道府県 (JIS X 0401 2桁コード, 例: 栃木=09)。gBizInfo 法人検索の
  // 地域優先絞り込みに使用。未設定なら地域優先せず従来の全国検索 (後方互換)。
  homePrefecture?: string;
  // PRODUCT_DEV-641 テナントブランドロゴの S3 キー（AdminServiceStack の SharedFiles バケット /
  // `tenant-logos/<tenantId>/<version>.<ext>`）。後方互換のため optional で、
  // **未設定なら従来どおり製品ロゴだけを出す**。キー自体に version が入る
  // イミュータブル方式なので、別途バージョン属性は持たない（規約は lib/tenantLogo.ts）。
  logoKey?: string;
  // PRODUCT_DEV-641 ログイン画面の ID 入力欄の呼び方（例: 「行員番号」）。未設定なら
  // 従来の文言（エイリアスログインなら「エイリアス」、通常なら「ログインID」）。
  // ⚠ **ログイン前の誰でも見られる**（tenantId さえ分かれば引ける）。固有名詞を入れない。
  loginIdLabel?: string;
  // PRODUCT_DEV-1070 初期表示ページ。未設定ならマイ手帳（後方互換のため optional）
  homeScreenDefault?: TenantHomeScreen;
  updatedAt: string;
  createdAt: string;
}
export interface DDBUserTenant {
  userId: string; // partition key
  tenantId: string;
  role: WhoamiUserRole;
  specialFlags?: string[]; // 特殊権限（例: ["sysadmin", "bypassIpRestriction"]）
  mfaRequired?: boolean; // ユーザー単位MFA強制フラグ
  status: string; // "active" | "disabled"
  updatedAt: string;
  createdAt: string;
}
export interface DDBProduct {
  productId: string; // partition key
  productName: string;
  productUrl: string;
  status: string;
  updatedAt: string;
  createdAt: string;
}
/**
 * PRODUCT_DEV-379: テナント設定の Webhook 通知設定
 * 文字起こしに notification keywords が含まれたとき Teams 等へ通知する。
 */
export interface NotificationWebhookConfig {
  enabled: boolean;
  destinationType: "teams"; // 将来: "slack" 等を union で拡張
  webhookUrl: string;
  keywords: string[]; // 部分一致対象（最大20語、各50文字）
}

/**
 * PRODUCT_DEV-531: テナント単位の Sansan 連携設定。
 *
 * **APIキーそのものはここに持たない。** 値は SSM Parameter Store の SecureString
 * （`/{env}/unizo/sansan/tenants/{tenantId}/api-key`）に置き、ここには「登録済みか」と
 * 「最後に疎通確認した結果」だけを残す（RULES Rule5: 秘匿情報を平文で保存しない）。
 *
 * 登録名義は**商談の記録者**で、記録者の Unizo のメールアドレスと同じ Sansan ユーザを
 * 登録のたびに探す（Sansan の ID はここに保存しない）。見つからないときの扱いだけを
 * テナントが決める（`ownerPolicy`）。API キーと同じ画面・同じシステム管理者が持つので
 * 同じ項目に入れており、**連携を解除すると一緒に消える**。
 */
export interface SansanIntegrationConfig {
  apiKeyRegisteredAt: string; // APIキーを SSM へ登録した日時(ISO)
  lastCheckedAt?: string; // 最後に接続確認した日時(ISO)
  lastCheckOk?: boolean; // その結果
  /**
   * 記録者の Sansan アカウントが見つからないとき。未設定は "error"（登録しない）。
   * "fallback" は `fallbackOwner` の名義で登録する。
   */
  ownerPolicy?: SansanOwnerPolicy;
  /**
   * 記録者の代わりに名義にするユーザ（テナントの Unizo ユーザ）。Sansan 側は登録のたびに
   * この人のメールアドレスで探し直す。部署ごとに変えたくなったら、ここと並べて
   * 部署 ID ごとの指定を足す（無ければこのテナントの指定を使う）。
   */
  fallbackOwner?: SansanFallbackOwner;
}

export type SansanOwnerPolicy = "error" | "fallback";

export interface SansanFallbackOwner {
  unizoUserId: string;
  name: string; // 保存した時点の表示名（画面の表示用）
}

export interface DDBTenantProduct {
  tenantId: string; // partition key
  productId: string;
  allowedIPAddresses?: string[]; // IP制限（オプション）
  featureFlags?: Record<string, boolean>; // テナント×プロダクト単位の機能フラグ
  notificationWebhook?: NotificationWebhookConfig; // PRODUCT_DEV-379
  sansanIntegration?: SansanIntegrationConfig; // PRODUCT_DEV-531
  /**
   * PRODUCT_DEV-531: テナントが Sansan 連携を使うか（テナント設定のトグル）。機能フラグ
   * `sansanContactRegistration`（運営が開ける）とは別の値で、商談の登録ボタンは両方が ON のときだけ出す。
   * `sansanIntegration` の中に入れないのは、API キーが無くても持て、連携を解除しても残すため。
   * 未設定は「API キーが登録済みなら使う」とみなす（`./sansanEnabled.ts` の `isSansanIntegrationEnabled`）。
   */
  sansanEnabled?: boolean;
  /**
   * PRODUCT_DEV-1096: 管理者（records.readAll）が他の人のメモを見られるか。システム管理者がテナント設定で切り替える。
   * PRODUCT_DEV-1117: ToDo も同じこの値で切り替える（名前はメモのままにしている。DB の項目名を変えると移行が要るため）。
   * **未設定は見られる**（入れる前からのテナントは今までどおり）。判定は `./adminMemoReadAll.ts` の `isAdminMemoReadAllEnabled`。
   * 議事録・商談記録の全員閲覧には効かない（メモ・ToDoだけ）。
   */
  adminMemoReadAll?: boolean;
  /**
   * PRODUCT_DEV-1203: 商談記録の充足度を測るか（PRODUCT_DEV-1088 の採点）。システム管理者がテナント設定で切り替える。
   * **未設定は測る**。OFF なら採点の Lambda が商談を読まず、記録を AI（Bedrock）に送らない。
   * 判定は `./meetingScoring.ts` の `isMeetingScoringEnabled`（明示の true か未設定だけ ON）。
   */
  meetingScoring?: boolean;
  updatedAt: string;
  createdAt: string;
}

/**
 * リクエストボディ, レスポンス型
 */
// auth-issue-code.ts

interface AuthApiRequestBody {
  tenantId: string;
}
export interface AuthIssueCodeRequestBody {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  productId: string;
  tenantId: string;
  state?: string; // CSRF対策やルーティング用
  region?: string; // 保存先のDDBリージョン
}
export interface AuthIssueCodeResponseBody {
  code: string;
  expiresIn: number; // seconds
}
export interface AuthIssueCodeResponse {
  success: boolean;
  body: AuthIssueCodeResponseBody;
}

// auth-exchange-code.ts
export interface AuthExchangeCodeRequestBody {
  code: string;
  region?: string; // 保存先のDDBリージョン
}
export interface AuthExchangeCodeResponseBody {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  tenantId: string;
}
export interface AuthExchangeCodeResponse {
  success: boolean;
  body: AuthExchangeCodeResponseBody;
}

// auth-refresh.ts
export interface AuthRefreshRequestBody {
  refreshToken: string;
  region?: string; // Cognitoリージョン
}
export interface AuthRefreshResponseBody {
  idToken: string;
  accessToken: string;
  refreshToken?: string; // Cognitoの設定次第で返却される
  expiresIn: number; // 秒単位
}
export interface AuthRefreshResponse {
  success: boolean;
  body: AuthRefreshResponseBody;
}

// whoami.ts
export type WhoamiUserRole = "admin" | "member" | "viewer";
export interface WhoamiUser {
  id: string;
  name: string;
  email?: string;
  alias?: string; // PRODUCT_DEV-421
  faceImageKey?: string; // PRODUCT_DEV-615 顔写真(アバター)の S3 キー。未設定=無し
}
export interface WhoamiProduct {
  id: string;
  name: string;
  url: string;
  featureFlags?: Record<string, boolean>;
  /** PRODUCT_DEV-531 テナントが Sansan 連携を使うか（`isSansanIntegrationEnabled` の結果） */
  sansanEnabled?: boolean;
}
export interface WhoamiMembership {
  tenantId: string;
  tenantName: string;
  role: WhoamiUserRole;
  products: WhoamiProduct[];
  mfaRequired?: boolean;
  specialFlags?: string[];
  // PRODUCT_DEV-396 パスキー設定 (テナント設定で判定)
  passkeyEnabled?: boolean; // tenant.passkeyEnabled の解決済み値
  tenantPasskeyEnabled?: boolean; // tenant.passkeyEnabled の生値 (UI 表示用)
  /**
   * PRODUCT_DEV-1070 初期表示ページ（tenant.homeScreenDefault）。未設定なら載らない。
   * アプリを開いた瞬間の振り分けで読むため whoami に載せる（設定 API を待てない）。
   */
  homeScreenDefault?: TenantHomeScreen;
}
export type WhoamiRequestBody = Record<string, never>;
export interface WhoamiMe {
  user: WhoamiUser;
  memberships: WhoamiMembership[];
  currentTenantId?: string;
  info: DDBUserProductInfo | undefined;
  lastPasskeyPromptedAt?: string; // PRODUCT_DEV-396 パスキー促進モーダルの30日判定用
  /**
   * PRODUCT_DEV-396 現在のリクエストUAが passkeyDeviceKeys に含まれているか
   * フロントの promote modal 判定用 (false なら「このブラウザでも登録しよう」と促す)
   */
  currentDeviceHasPasskey?: boolean;
}
export interface WhoamiResponse {
  success: boolean;
  me: WhoamiMe;
}

/**
 * TenantUsers API - テナント内のユーザを取得するAPI
 * GET /api/tenant/{tenantId}/users
 * src: /shared-domain/auth-service/lambda/tenantUsers.ts
 */
export type TenantUsersRequestBody = AuthApiRequestBody & {
  useCache?: string;
};

export type TenantUsers = {
  userId: string;
  name: string;
  /**
   * PRODUCT_DEV-811: **当社スタッフ（`selectable: false`）では省略される。**
   * 顧客テナントの一般メンバーもこの API を叩けるので、名前解決に要らない項目は返さない。
   * 空文字でごまかすと型では気づけないので、プロパティごと落として optional にしてある。
   */
  email?: string;
  /** PRODUCT_DEV-615 顔写真(アバター)の S3 キー。未設定=無し。当社スタッフでは省略される。 */
  faceImageKey?: string;
  /**
   * PRODUCT_DEV-811: **一覧・選択肢に出してよい人か**。当社スタッフは顧客テナントで false。
   *
   * 配列からは外さない。外すと当社が顧客テナントで記録した過去の商談・議事録の
   * 作成者名が「(ユーザ未登録)」になるため、**名前解決には全員を使う**。
   * 未設定は true 扱い（後方互換）。
   */
  selectable?: boolean;
};

export type TenantUsersResponse = {
  success: boolean;
  users: TenantUsers[];
};

/**
 * Me API - ログインユーザの情報を更新するAPI
 * PATCH /api/auth/me
 * src: /shared-domain/auth-service/lambda/tenantUsers.ts
 */

export type PatchAuthMeRequestBody = AuthApiRequestBody & DDBPatchCurrentTenant;

export type PatchAuthMeResponse = {
  success: boolean;
  user: DDBUser;
};

/**
 * 管理画面用: ユーザー情報
 */
export interface AdminUser {
  userId: string;
  username: string;
  name: string;
  email: string;
  alias?: string; // PRODUCT_DEV-421
  role: "admin" | "member" | "viewer";
  specialFlags?: string[];
  mfaRequired?: boolean;
  mfaActive?: boolean; // CognitoのTOTP MFA有効状態
  status: string;
  lastLoginDate?: string; // 最終ログイン日時（ISO文字列）
  createdAt: string;
  updatedAt: string;
}

/**
 * 管理画面用: ユーザー一覧レスポンス
 */
export interface AdminUsersResponse {
  success: boolean;
  users: AdminUser[];
}

/**
 * 管理画面用: ユーザー作成リクエスト
 */
export interface CreateUserRequest {
  name: string;
  email?: string;
  username?: string;
  alias?: string; // PRODUCT_DEV-421
  role: "admin" | "member" | "viewer";
}

/**
 * 管理画面用: ユーザー作成レスポンス
 */
export interface CreateUserResponse {
  success: boolean;
  user: AdminUser;
  temporaryPassword?: string;
  // PRODUCT_DEV-508: email が他テナントで既登録のため、新規作成せず既存ユーザを
  // 当該テナントへ紐付けた場合に true
  linkedExisting?: boolean;
  // PRODUCT_DEV-535: linkedExisting のうち、既存ユーザが未ログイン（削除し損ねた残骸）
  // だったため新規登録として扱い、仮パスワードを再発行して招待メールを送った場合に true。
  // false のときは他テナントで現役のアカウントを紐付けただけで、招待メール・仮パスワードは
  // 発行されず表示名も既存値のまま
  invitationSent?: boolean;
}


/**
 * 管理画面用: ユーザー削除レスポンス（action="permanently-delete"）
 */
export interface DeleteUserResponse {
  success: boolean;
  // PRODUCT_DEV-535: Cognito アカウントごと完全に削除できたか。
  // 他テナントにも所属しているユーザーは Cognito を消せない（他テナントのログインが
  // 壊れる）ため、そのテナントからの除名のみとなり false になる
  deletedCompletely?: boolean;
}

/**
 * 管理画面用: ユーザー詳細取得レスポンス
 */
export interface AdminUserDetailResponse {
  success: boolean;
  user: AdminUser;
}

/**
 * 管理画面用: ユーザー更新リクエスト
 */
export interface UpdateUserRequest {
  action: "update" | "change-email" | "disable" | "enable" | "permanently-delete" | "reset-mfa" | "set-alias";
  name?: string; // action="update"の場合
  role?: "admin" | "member" | "viewer"; // action="update"の場合
  mfaRequired?: boolean; // action="update"の場合
  specialFlags?: string[]; // action="update"の場合
  email?: string; // action="change-email"の場合
  alias?: string; // action="set-alias"の場合 (PRODUCT_DEV-421)
}

/**
 * 管理画面用: ユーザー更新レスポンス
 */
export interface UpdateUserResponse {
  success: boolean;
  user: AdminUser;
  temporaryPassword?: string; // action="change-email"の場合のみ
  mfaMessage?: string; // MFA無効化できない場合のメッセージ
  mfaDisabled?: boolean; // Cognito MFAを自動無効化した場合true
}

/**
 * 管理画面用: パスワード再設定レスポンス
 */
export interface ResetPasswordResponse {
  success: boolean;
  emailSent?: boolean; // メール送信した場合
  email?: string; // メール送信した場合のアドレス
  temporaryPassword?: string; // メールなしの場合の仮パスワード
}

/**
 * テナント設定レスポンス
 */
export interface TenantSettingsResponse {
  success: boolean;
  tenant: {
    tenantId: string;
    tenantName: string;
  };
  allowedIPAddresses: string[];
  mfaRequired?: boolean;
  passkeyEnabled?: boolean; // PRODUCT_DEV-396
  notificationWebhook?: NotificationWebhookConfig; // PRODUCT_DEV-379
  homePrefecture?: string; // PRODUCT_DEV-611 本社都道府県 (JIS 2桁コード)
  // PRODUCT_DEV-531 Sansan 連携の登録状況。**APIキーは絶対に含めない**
  sansanIntegration?: SansanIntegrationConfig;
  // PRODUCT_DEV-531 Sansan 連携を使うか（未設定の解釈を済ませた値）
  sansanEnabled?: boolean;
  // PRODUCT_DEV-889 ログイン画面の表示。未設定なら null（後方互換のため optional）
  loginIdLabel?: string | null;
  /** 設定済みロゴの表示用 presigned GET URL。未設定・取得失敗なら null */
  logoUrl?: string | null;
  // PRODUCT_DEV-1070 初期表示ページ。未設定なら null（後方互換のため optional）
  homeScreenDefault?: TenantHomeScreen | null;
  // PRODUCT_DEV-1096 管理者が他の人のメモを見られるか（未設定の解釈を済ませた値）。システム管理者にだけ返す
  adminMemoReadAll?: boolean;
  // PRODUCT_DEV-1203 商談記録の充足度を測るか（未設定の解釈を済ませた値）。システム管理者にだけ返す
  meetingScoring?: boolean;
}

/**
 * PRODUCT_DEV-531: Sansan 連携設定の更新リクエスト。
 * - `apiKey` あり → SSM へ保存（保存前に必ず Sansan へ疎通確認する）
 * - `apiKey` 省略  → 既存のキーを使ったまま、接続確認だけ行う
 * - `clear: true` → SSM のパラメータを削除し、設定を解除する
 * - `owner` あり   → 登録の名義の設定だけを保存する（API キーは触らない・接続確認もしない）
 */
export interface UpdateSansanIntegrationRequest {
  apiKey?: string;
  clear?: boolean;
  owner?: {
    policy: SansanOwnerPolicy;
    /** policy が "fallback" のとき必須。テナントの Unizo ユーザ ID */
    fallbackOwnerUnizoUserId?: string;
  };
}

/**
 * PRODUCT_DEV-531: 「指定したユーザー」の候補（テナント設定 › 外部連携 › Sansan連携）。
 * テナントの Unizo ユーザのうち、同じメールアドレスの Sansan アカウントを持つ人だけ。
 * **Sansan 側の情報（ID・メールアドレスの一覧）は返さない。**
 */
export interface SansanOwnerCandidatesResponse {
  candidates: { unizoUserId: string; name: string; email: string }[];
  /** 保存済みの指定したユーザーが今も候補にいるか。未設定なら null */
  fallbackOwnerAvailable: boolean | null;
}

/**
 * テナント設定更新リクエスト
 */
export interface UpdateTenantSettingsRequest {
  allowedIPAddresses?: string[];
  mfaRequired?: boolean;
  passkeyEnabled?: boolean; // PRODUCT_DEV-396
  notificationWebhook?: NotificationWebhookConfig; // PRODUCT_DEV-379
  // PRODUCT_DEV-611 本社都道府県 (JIS 2桁コード)。空文字は未設定に戻す。
  homePrefecture?: string;
  sansanIntegration?: UpdateSansanIntegrationRequest; // PRODUCT_DEV-531
  // PRODUCT_DEV-531 Sansan 連携を使う／使わない（API キーと名義の設定はそのまま残る）
  sansanEnabled?: boolean;
  // PRODUCT_DEV-889 ログイン画面の ID 欄の呼び方。null または空文字で未設定に戻す
  loginIdLabel?: string | null;
  // PRODUCT_DEV-1070 初期表示ページ。null で未設定（マイ手帳）に戻す
  homeScreenDefault?: TenantHomeScreen | null;
  // PRODUCT_DEV-1096 管理者が他の人のメモを見られるか。システム管理者だけが変えられる
  adminMemoReadAll?: boolean;
  // PRODUCT_DEV-1203 商談記録の充足度を測るか。システム管理者だけが変えられる
  meetingScoring?: boolean;
}

/** PRODUCT_DEV-889 テナント設定: ロゴのアップロード URL 発行レスポンス */
export interface TenantLogoUploadUrlResponse {
  success: boolean;
  uploadUrl: string;
  s3Key: string;
}

/**
 * テナント設定更新レスポンス
 */
export interface UpdateTenantSettingsResponse {
  success: boolean;
}

/**
 * PRODUCT_DEV-396 ログイン画面用 public API
 * メアド入力後、サーバーから「パスキー利用可否」を取得しログインボタン表示制御
 * POST /api/auth/login-options
 *
 * セキュリティ:
 * - 認証不要 (public)
 * - 存在しないメアドでも 200 + { passkeyEnabled: false } を返す（ユーザ列挙対策）
 * - レスポンス時間を一定化
 */
export interface LoginOptionsRequest {
  email: string;
}

export interface LoginOptionsResponse {
  success: boolean;
  passkeyEnabled: boolean;
}

/**
 * PRODUCT_DEV-421 エイリアスログイン用 public API
 * テナントIDとエイリアスから cognitoUsername を解決する。
 * POST /api/auth/resolve-alias
 *
 * セキュリティ:
 * - 認証不要 (public)
 * - alias 不存在やテナント不正でも 200 + ダミー cognitoUsername (`__notfound__.{tenantId}`) を返す（ユーザ列挙対策）
 * - レスポンス時間を一定化
 */
export interface ResolveAliasRequest {
  tenantId: string;
  alias: string;
}

/**
 * alias 解決失敗時に cognitoUsername へ付与するダミー接頭辞。
 * サーバは `${RESOLVE_ALIAS_NOT_FOUND_PREFIX}${tenantId}` を返し、
 * クライアントはこの接頭辞で「alias 未解決」を判定して通常ログインID にフォールバックする。
 */
export const RESOLVE_ALIAS_NOT_FOUND_PREFIX = "__notfound__.";

/**
 * PRODUCT_DEV-641 ログイン画面のテナントブランド解決（**認証不要**）。
 *
 * `ResolveAliasRequest` と同じく、**存在しないテナントでも 200 を返す**（テナントの
 * 列挙を許さない）。返すのはロゴの presigned URL だけで、**テナント名は返さない**
 * —— 未認証で tenantId からテナント名が引けると、それ自体が列挙の材料になる。
 */
export interface TenantBrandingRequest {
  tenantId: string;
}

/**
 * ロゴが未設定・テナントが存在しない・取得に失敗した、のいずれでも `logoUrl: null`。
 * **呼ぶ側はこの3つを区別できない**（区別できると列挙できてしまう）。
 */
export interface TenantBrandingResponse {
  success: boolean;
  /** インライン表示用の presigned GET URL。未設定なら null。 */
  logoUrl: string | null;
  /**
   * ID 入力欄の呼び方（例: 「行員番号」）。未設定なら null で、画面は従来の文言を使う。
   *
   * **テナント名は返さない**（tenantId から会社名を引けるようにしない）。
   * ⚠ この値は**未認証で誰でも引ける**。設定するのはテナントの sysadmin（Unizo の
   * テナント設定、PRODUCT_DEV-889）なので、固有名詞を入れないことは設定画面の
   * 注意書きで伝える（機械的な強制は無い）。
   */
  loginIdLabel: string | null;
}

export interface ResolveAliasResponse {
  success: boolean;
  cognitoUsername: string;
  /**
   * PRODUCT_DEV-484 解決したユーザがこのブラウザ(UA)でパスキー利用可能か。
   * alias 未解決時やパスキー未対応時は false。エイリアスログイン画面での
   * パスキー自動発動に使う（email を露出させずに可否のみ返す）。
   */
  passkeyEnabled?: boolean;
}
